import { json, type MetaFunction } from '@remix-run/node';
import { Link, Form, useActionData, useNavigation, useLoaderData } from '@remix-run/react';
import { useState, useEffect } from 'react';
import type { ActionFunction, LoaderFunction } from '@remix-run/node';
import { sendConsultationEmail } from '~/utils/email.server';
import { db } from '~/lib/db.server';
import { serializePrismaData } from '~/lib/utils';
import type { lawyers } from '@prisma/client';

// Google Analytics gtag 타입 선언
declare global {
    interface Window {
        gtag: (command: string, targetId: string, config?: Record<string, any>) => void;
    }
}

export const handle = {
    disableRootLayout: true
};

export const meta: MetaFunction = () => {
    return [
        { title: '법무법인 휘상 | 이혼 및 가사 사건 전문' },
        { name: 'description', content: '법무법인 휘상은 이혼, 양육권, 재산분할, 상속 등 가사 사건에서 전문적인 법률서비스를 제공합니다.' },
        { name: 'keywords', content: '이혼, 이혼변호사, 법무법인, 가사소송, 양육권, 재산분할, 위자료, 상속, 가사분쟁, 법무법인 휘상' },
        { tagName: 'link', rel: 'canonical', href: 'https://jp-hwisang.co.kr/landing-divorce' },
        { tagName: 'link', rel: 'preconnect', href: 'https://juxieynkvlznzffumzui.supabase.co' },
        { tagName: 'link', rel: 'dns-prefetch', href: 'https://juxieynkvlznzffumzui.supabase.co' },
        { property: 'og:title', content: '법무법인 휘상 | 이혼 및 가사 사건 전문' },
        { property: 'og:description', content: '법무법인 휘상은 이혼, 양육권, 재산분할, 상속 등 가사 사건에서 전문적인 법률서비스를 제공합니다.' },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: 'https://jp-hwisang.co.kr/landing-divorce' },
        { property: 'og:image', content: 'https://jp-hwisang.co.kr/images/og-image.jpg' }
    ];
};

export const action: ActionFunction = async ({ request }) => {
    const formData = await request.formData();
    const name = formData.get('name')?.toString();
    const phone = formData.get('phone')?.toString();
    const message = formData.get('message')?.toString();
    const surveyData = formData.get('surveyData')?.toString();

    // 유효성 검사
    if (!name || !phone) {
        return json({ success: false, error: '모든 필드를 입력해주세요.' });
    }

    try {
        // 설문 데이터를 메시지에 포함
        let fullMessage = message || '';
        if (surveyData) {
            const survey = JSON.parse(surveyData);
            fullMessage += '\n\n=== 설문 응답 ===\n';
            fullMessage += `현재 상황: ${survey.situation}\n`;
            fullMessage += `이혼 사유: ${survey.reason}\n`;
            fullMessage += `자녀 유무: ${survey.children}\n`;
            fullMessage += `재산 현황: ${survey.property}\n`;
            fullMessage += `우선 목표: ${survey.priority}\n`;
        }

        // 이메일 전송
        const emailResult = await sendConsultationEmail({
            name,
            phone,
            message: fullMessage,
        });

        if (emailResult.success) {
            console.log('상담 이메일 전송 성공');
            return json({ success: true });
        } else {
            console.error('이메일 전송 오류:', emailResult);
            return json({
                success: true,
                serverMessage: '이메일 전송에 문제가 있지만 상담 데이터는 저장되었습니다.'
            });
        }
    } catch (error) {
        console.error('이메일 전송 오류:', error);
        if (process.env.NODE_ENV === 'development') {
            return json({ success: false, error: '이메일 전송 중 오류가 발생했습니다.' });
        } else {
            return json({ success: true });
        }
    }
};

export const loader: LoaderFunction = async () => {
    const lawyers = await db.lawyers.findMany({
        where: {
            deleted_at: null,
            is_active: true,
        },
        orderBy: {
            order: 'asc',
        },
    });

    // BigInt 및 Date 등의 값을 안전하게 직렬화
    const serializedLawyers = serializePrismaData(lawyers);

    return json({ lawyers: serializedLawyers });
};

// 설문지 데이터 타입
interface SurveyData {
    situation: string;
    reason: string;
    children: string;
    property: string;
    priority: string;
}

export default function LandingDivorce() {
    const { lawyers } = useLoaderData<typeof loader>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [consultType, setConsultType] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [currentLawyerIndex, setCurrentLawyerIndex] = useState(0);
    const [surveyData, setSurveyData] = useState<SurveyData>({
        situation: '',
        reason: '',
        children: '',
        property: '',
        priority: ''
    });
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        message: '',
    });

    const actionData = useActionData<typeof action>();
    const navigation = useNavigation();
    const isSubmitting = navigation.state === 'submitting';

    // 현재 보여줄 변호사들 (3명씩)
    const lawyersPerPage = 3;
    const currentLawyers = lawyers.slice(currentLawyerIndex, currentLawyerIndex + lawyersPerPage);
    const hasNextLawyers = currentLawyerIndex + lawyersPerPage < lawyers.length;
    const hasPrevLawyers = currentLawyerIndex > 0;

    // 모바일용 변호사 네비게이션
    const [currentMobileLawyerIndex, setCurrentMobileLawyerIndex] = useState(0);
    const hasNextMobileLawyer = currentMobileLawyerIndex < lawyers.length - 1;
    const hasPrevMobileLawyer = currentMobileLawyerIndex > 0;

    const nextLawyers = () => {
        if (hasNextLawyers) {
            setCurrentLawyerIndex(prev => prev + lawyersPerPage);
        }
    };

    const prevLawyers = () => {
        if (hasPrevLawyers) {
            setCurrentLawyerIndex(prev => Math.max(0, prev - lawyersPerPage));
        }
    };

    const nextMobileLawyer = () => {
        if (hasNextMobileLawyer) {
            setCurrentMobileLawyerIndex(prev => prev + 1);
        }
    };

    const prevMobileLawyer = () => {
        if (hasPrevMobileLawyer) {
            setCurrentMobileLawyerIndex(prev => prev - 1);
        }
    };

    // 설문지 질문과 선택지 데이터
    const surveyQuestions = [
        {
            id: 'situation',
            question: '현재 어떤 상황인가요?',
            options: [
                '배우자의 불륜 사실을 최근 발견했다',
                '불륜 관계가 지속되고 있는 중이다',
                '불륜 관계는 종료되었으나 법적 조치를 원한다',
                '불륜 증거를 확보했으나 어떻게 진행해야 할지 모르겠다',
                '성격 차이로 인한 이혼을 고려 중이다',
                '가정폭력이나 경제적 문제가 있다'
            ]
        },
        {
            id: 'reason',
            question: '이혼을 고려하는 주된 이유는 무엇인가요?',
            options: [
                '배우자의 외도/불륜',
                '가정폭력 (신체적/정신적)',
                '성격 차이 및 가치관 충돌',
                '경제적 문제 (도박, 사업실패 등)',
                '시댁/처가 문제',
                '배우자의 알코올 중독이나 기타 중독',
                '기타 사유'
            ]
        },
        {
            id: 'children',
            question: '자녀가 있으신가요?',
            options: [
                '미성년 자녀가 있다 (양육권이 중요)',
                '성인 자녀만 있다',
                '자녀가 없다',
                '임신 중이다',
                '양육권을 포기할 의향이 있다',
                '공동양육을 희망한다'
            ]
        },
        {
            id: 'property',
            question: '현재 확보하신 증거자료나 재산 현황은 어떤가요?',
            options: [
                '직접 목격 (현장 발견)',
                '통화/문자/SNS 기록',
                '사진/영상 자료',
                '제3자의 증언',
                '재산 목록을 파악하고 있다',
                '현재 증거가 부족한 상황이다'
            ]
        },
        {
            id: 'priority',
            question: '가장 우선적으로 원하는 결과는 무엇인가요?',
            options: [
                '빠른 진행과 해결',
                '적절한 금전적 배상 (위자료, 재산분할)',
                '상대방에 대한 법적 책임 추궁',
                '자녀에게 미치는 영향 최소화',
                '개인정보와 프라이버시 보호',
                '원만한 합의를 통한 해결'
            ]
        }
    ];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleConsultClick = (type: string) => {
        setConsultType(type);
        setCurrentStep(1);
        // 설문 데이터 초기화
        setSurveyData({
            situation: '',
            reason: '',
            children: '',
            property: '',
            priority: ''
        });
    };

    const handleSurveyAnswer = (questionId: string, answer: string) => {
        setSurveyData(prev => ({ ...prev, [questionId]: answer }));

        // 마지막 질문이 아니면 다음 단계로
        if (currentStep < surveyQuestions.length) {
            setTimeout(() => {
                setCurrentStep(prev => prev + 1);
            }, 300);
        } else {
            // 마지막 질문이면 완료 화면으로
            setTimeout(() => {
                setCurrentStep(prev => prev + 1);
            }, 300);
        }
    };

    const handleSurveyBack = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
        }
    };

    // UTM 파라미터 추적 및 분석
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const utmSource = params.get('utm_source');
        const utmMedium = params.get('utm_medium');
        const utmCampaign = params.get('utm_campaign');
        const utmContent = params.get('utm_content');
        const utmTerm = params.get('utm_term');

        // UTM 파라미터들을 세션스토리지에 저장
        if (utmSource) sessionStorage.setItem('utm_source', utmSource);
        if (utmMedium) sessionStorage.setItem('utm_medium', utmMedium);
        if (utmCampaign) sessionStorage.setItem('utm_campaign', utmCampaign);
        if (utmContent) sessionStorage.setItem('utm_content', utmContent);
        if (utmTerm) sessionStorage.setItem('utm_term', utmTerm);

        // Google Analytics에 UTM 파라미터 전송
        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'page_view', {
                page_type: 'divorce_landing',
                page_category: 'landing_page',
                utm_source: utmSource || sessionStorage.getItem('utm_source'),
                utm_medium: utmMedium || sessionStorage.getItem('utm_medium'),
                utm_campaign: utmCampaign || sessionStorage.getItem('utm_campaign'),
                utm_content: utmContent || sessionStorage.getItem('utm_content'),
                utm_term: utmTerm || sessionStorage.getItem('utm_term')
            });
        }
    }, []);

    // 폼 제출 완료 시 처리
    useEffect(() => {
        if (actionData?.success && !isSubmitting) {
            // Google Analytics 상담 신청 이벤트 추적
            if (typeof window !== 'undefined' && window.gtag) {
                window.gtag('event', 'consultation_request', {
                    event_category: 'engagement',
                    event_label: 'divorce_consultation',
                    page_type: 'divorce_landing',
                    utm_source: sessionStorage.getItem('utm_source'),
                    utm_medium: sessionStorage.getItem('utm_medium'),
                    utm_campaign: sessionStorage.getItem('utm_campaign')
                });
            }

            setFormData({ name: '', phone: '', message: '' });
            setSurveyData({
                situation: '',
                reason: '',
                children: '',
                property: '',
                priority: ''
            });
            setIsModalOpen(false);
            alert('상담 신청이 완료되었습니다. 빠른 시일 내에 연락드리겠습니다.');
        } else if (actionData?.error) {
            alert(actionData.error);
        }
    }, [actionData, isSubmitting]);

    // 변호사 특징 데이터
    const lawyerFeatures = [
        { icon: "⚖️", title: "풍부한 경력의 변호사 직접 상담 진행", desc: "풍부한 경험을 가진 전문 변호사가 처음부터 끝까지 직접 상담합니다." },
        { icon: "📊", title: "가정법원 네트워크 보유", desc: "각 지방 법원별 특성을 파악하고 맞춤형 전략을 제공합니다." },
        { icon: "💼", title: "재산분할 영역 압도적 승소사례 보유", desc: "숨겨진 재산 발견 및 정확한 재산 평가로 최대한의 권익을 보호합니다." },
        { icon: "👪", title: "양육권 분쟁 전문", desc: "자녀의 복지를 최우선으로 고려한 양육권 및 양육비 협상을 지원합니다." },
        { icon: "🔍", title: "철저한 증거 수집", desc: "상간자 증거, 재산 증빙 등 소송에 필요한 모든 증거를 철저히 수집합니다." },
        { icon: "🤝", title: "원만한 협의 유도", desc: "불필요한 분쟁보다 상호 합의를 통한 효율적인 해결책을 모색합니다." }
    ];

    // 교육 정보 정렬 함수
    const sortEducation = (education: string[]) => {
        return [...education].sort((a, b) => {
            // 우선순위 점수 계산 (낮을수록 앞에 위치)
            const getPriority = (str: string) => {
                if (str.includes('고등학교')) return 1; // 1순위
                if (str.includes('대학원')) return 3;   // 3순위 - 대학원 포함 (대학교도 함께 포함됨)
                if (str.includes('대학교')) return 2;   // 2순위 - 대학교만 포함
                return 4; // 기타
            };

            const aPriority = getPriority(a);
            const bPriority = getPriority(b);

            return aPriority - bPriority;
        });
    };

    return (
        <>
            {/* Google Analytics 4 */}
            <script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
            <script
                dangerouslySetInnerHTML={{
                    __html: `
                        window.dataLayer = window.dataLayer || [];
                        function gtag(){dataLayer.push(arguments);}
                        gtag('js', new Date());
                        gtag('config', 'G-XXXXXXXXXX', {
                            page_title: document.title,
                            page_location: window.location.href,
                            custom_map: {
                                'custom_parameter_1': 'page_type'
                            }
                        });
                        
                        // 이혼 랜딩 페이지 방문 이벤트
                        gtag('event', 'page_view', {
                            page_type: 'divorce_landing',
                            page_category: 'landing_page'
                        });
                    `
                }}
            />

            {/* Smartlog 스크립트 */}
            <script
                type="text/javascript"
                dangerouslySetInnerHTML={{
                    __html: `var hpt_info={'_account':'UHPT-32118', '_server': 'a29'};`
                }}
            />
            <script
                type="text/javascript"
                src="//cdn.smlog.co.kr/core/smart.js"
                charSet="utf-8"
            />
            <noscript>
                <img
                    src="//a29.smlog.co.kr/smart_bda.php?_account=32118"
                    style={{ display: 'none', width: 0, height: 0 }}
                    alt=""
                />
            </noscript>

            <div className="landing-container">
                {/* 헤더 비디오 배경 섹션 */}
                <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-amber-900/20 to-yellow-900/25 z-10"></div>

                    {/* 배경 비디오 또는 이미지 */}
                    <div className="absolute inset-0 w-full h-full">
                        <video
                            className="w-full h-full object-cover brightness-75"
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="auto"
                            crossOrigin="anonymous"
                        >
                            <source
                                src="https://juxieynkvlznzffumzui.supabase.co/storage/v1/object/public/videos//divorce.mp4"
                                type="video/mp4"
                            />
                        </video>
                    </div>

                    {/* 헤더 콘텐츠 */}
                    <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto animate-fadeIn">
                        <div className="mb-8">
                            <div className="flex items-center justify-center mb-6">
                                <img
                                    src="/images/cube.png"
                                    alt="법무법인 휘상"
                                    className="h-6 md:h-8 w-auto mr-3 opacity-80"
                                />
                                <p className="text-sm md:text-base text-white/70 tracking-[0.3em] uppercase font-light">Law Firm Hwisang</p>
                            </div>
                            <h1 className="text-5xl md:text-7xl lg:text-8xl font-light mb-4 leading-tight tracking-tight">
                                <span className="font-bold">이혼</span>과 <span className="font-bold">상속</span>
                            </h1>
                            <div className="h-1 w-32 bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-8 opacity-60"></div>
                            <p className="text-2xl md:text-3xl lg:text-4xl font-light text-gray-100 tracking-wide">
                                가족 간의 분쟁에서 가져야 할 <span className="font-medium">용기</span>
                            </p>
                        </div>

                        <p className="text-lg md:text-xl lg:text-2xl mb-12 max-w-3xl mx-auto font-light leading-relaxed tracking-wide text-gray-100">
                            <span className="block mb-4">
                                <span className="text-3xl md:text-4xl lg:text-3xl font-bold bg-gradient-to-r from-white via-yellow-100 to-white bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(255,255,255,0.5)] animate-glow">
                                    법무법인 휘상
                                </span>
                                <span className="text-xl md:text-2xl lg:text-3xl">이</span>
                            </span>
                            <span className="text-xl md:text-2xl lg:text-3xl font-medium">힘 있는 용기</span>가 되어드리겠습니다.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
                            <button
                                onClick={() => document.getElementById('survey-section')?.scrollIntoView({ behavior: 'smooth' })}
                                className="group relative px-10 py-5 bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white hover:text-gray-900 text-white rounded-full text-lg font-medium transition-all duration-300 overflow-hidden"
                            >
                                <span className="relative z-10">무료 법률상담 신청</span>
                                <div className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                            <a
                                href="tel:02-6959-2170"
                                className="group px-10 py-5 bg-transparent border-2 border-white/50 hover:border-white hover:bg-white/20 backdrop-blur-sm text-white rounded-full text-lg font-medium transition-all duration-300 flex items-center justify-center gap-3"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                </svg>
                                <span>전화 상담하기</span>
                            </a>
                        </div>
                    </div>

                    {/* 스크롤 다운 아이콘 */}
                    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
                        <div className="flex flex-col items-center gap-2">
                            <p className="text-white/60 text-sm tracking-widest uppercase">Scroll</p>
                            <div className="w-7 h-12 border-2 border-white/30 rounded-full flex justify-center p-2">
                                <div className="w-1 h-3 bg-white/60 rounded-full animate-scroll"></div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 설문지 섹션 */}
                <section id="survey-section" className="py-20" style={{ background: 'linear-gradient(135deg, #fefefe 0%, #fef7f7 100%)' }}>
                    <div className="max-w-4xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">
                                상황 파악을 위한 <span className="text-red-600">간단한 질문</span>
                            </h2>
                            <p className="text-lg text-gray-600">
                                더 정확한 상담을 위해 현재 상황을 알려주세요
                            </p>
                        </div>

                        {/* 설문지 UI */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                            {/* 진행률 헤더 */}
                            <div className="bg-gradient-to-r from-red-600 to-red-800 p-6 text-white">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-xl font-bold">
                                            질문 {Math.min(currentStep, surveyQuestions.length)} / {surveyQuestions.length}
                                        </h3>
                                        <p className="text-red-100">
                                            진행률: {Math.min(Math.round(((currentStep - 1) / surveyQuestions.length) * 100), 100)}%
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-3xl font-bold">{Math.min(currentStep, surveyQuestions.length)}</div>
                                        <div className="text-sm text-red-200">/ {surveyQuestions.length}</div>
                                    </div>
                                </div>

                                {/* 진행률 바 */}
                                <div className="w-full bg-red-800/30 rounded-full h-3">
                                    <div
                                        className="bg-white h-3 rounded-full transition-all duration-500 ease-out"
                                        style={{ width: `${Math.min(((currentStep - 1) / surveyQuestions.length) * 100, 100)}%` }}
                                    ></div>
                                </div>
                            </div>

                            {/* 질문 내용 */}
                            <div className="p-8">
                                {surveyQuestions[currentStep - 1] && currentStep <= surveyQuestions.length && (
                                    <div className="space-y-6">
                                        <h4 className="text-2xl md:text-3xl font-bold text-gray-800 leading-relaxed mb-8">
                                            {surveyQuestions[currentStep - 1].question}
                                        </h4>

                                        <div className="grid gap-4">
                                            {surveyQuestions[currentStep - 1].options.map((option, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleSurveyAnswer(surveyQuestions[currentStep - 1].id, option)}
                                                    className="w-full p-5 text-left border-2 border-gray-200 rounded-xl hover:border-red-500 hover:bg-red-50 transition-all duration-200 group survey-option-hover"
                                                >
                                                    <div className="flex items-center">
                                                        <div className="w-6 h-6 border-2 border-gray-300 rounded-full mr-4 group-hover:border-red-500 flex items-center justify-center transition-colors">
                                                            <div className="w-3 h-3 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                                        </div>
                                                        <span className="text-gray-700 group-hover:text-red-700 font-medium">
                                                            {option}
                                                        </span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 하단 네비게이션 */}
                            {currentStep <= surveyQuestions.length && (
                                <div className="bg-gray-50 px-8 py-6 flex justify-between items-center">
                                    <button
                                        onClick={handleSurveyBack}
                                        disabled={currentStep === 1}
                                        className="flex items-center px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                                        </svg>
                                        이전
                                    </button>

                                    <div className="text-center">
                                        <div className="text-sm text-gray-500 mb-1">진행 상황</div>
                                        <div className="flex space-x-2">
                                            {Array.from({ length: surveyQuestions.length }, (_, i) => (
                                                <div
                                                    key={i}
                                                    className={`w-3 h-3 rounded-full transition-colors ${i < currentStep ? 'bg-red-500' : 'bg-gray-300'
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    <div className="w-20"></div> {/* 균형을 위한 빈 공간 */}
                                </div>
                            )}
                        </div>

                        {/* 설문 완료 후 표시될 요약 */}
                        {currentStep > surveyQuestions.length && (
                            <div className="mt-8 bg-white rounded-xl shadow-lg p-8">
                                <h3 className="text-2xl font-bold text-center mb-6 text-red-600">설문이 완료되었습니다!</h3>
                                <div className="bg-red-50 p-6 rounded-lg mb-6">
                                    <h4 className="font-bold text-red-800 mb-4">응답 요약</h4>
                                    <div className="space-y-2 text-sm text-red-700">
                                        {surveyData.situation && <p>• 현재 상황: {surveyData.situation}</p>}
                                        {surveyData.reason && <p>• 이혼 사유: {surveyData.reason}</p>}
                                        {surveyData.children && <p>• 자녀 유무: {surveyData.children}</p>}
                                        {surveyData.property && <p>• 재산/증거: {surveyData.property}</p>}
                                        {surveyData.priority && <p>• 우선 목표: {surveyData.priority}</p>}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <button
                                        onClick={() => setIsModalOpen(true)}
                                        className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white rounded-lg text-lg font-bold transition-colors"
                                    >
                                        상담 신청하기
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* 상담 유형 선택 섹션 */}
                <section className="py-20 bg-white">
                    <div className="max-w-6xl mx-auto px-4">
                        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">어떤 부분이 고민이신가요?</h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                { id: 'divorce', title: '이혼소송', desc: '외도, 가정폭력, 성격차이 등 이혼 사유에 따른 맞춤 상담' },
                                { id: 'property', title: '재산분할', desc: '숨긴 재산 추적, 공정한 분할 비율 확보, 사업체 재산 평가 지원' },
                                { id: 'custody', title: '양육권', desc: '자녀 복지 최우선, 합리적인 양육비 산정 및 면접교섭권 조정' },
                                { id: 'alimony', title: '위자료', desc: '외도, 폭력 등 유책배우자에 대한 적정 위자료 청구 지원' },
                                { id: 'inheritance', title: '상속', desc: '유언장 검증, 상속재산 분할, 유류분 청구 등 종합 상속 서비스' },
                                { id: 'other', title: '기타 가사분쟁', desc: '가족 간 금전 분쟁, 부양의무, 친자관계 등 모든 가사 문제 해결' },
                            ].map(item => (
                                <div key={item.id} className="bg-gray-50 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300">
                                    <div className="p-8">
                                        <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                                        <p className="text-gray-600 mb-6">{item.desc}</p>
                                        <button
                                            onClick={() => document.getElementById('survey-section')?.scrollIntoView({ behavior: 'smooth' })}
                                            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded text-lg font-medium transition-colors"
                                        >
                                            상담 신청하기
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* 변호사 프로필 섹션 */}
                <section className="py-24 bg-white relative">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-20">
                            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
                                Our Legal Team
                            </h2>
                            <div className="w-24 h-0.5 bg-red-600 mx-auto mb-8"></div>
                            <p className="text-lg text-gray-600 max-w-2xl mx-auto font-light leading-relaxed">
                                경험과 전문성을 바탕으로 최상의 법률 서비스를 제공하는
                                법무법인 휘상의 변호사진을 소개합니다
                            </p>
                        </div>

                        {/* 변호사 그리드 */}
                        <div className="relative">
                            {/* 데스크탑 버전 - 3명씩 표시 */}
                            <div className="hidden lg:grid lg:grid-cols-3 gap-12">
                                {currentLawyers.map((lawyer: lawyers, index: number) => (
                                    <div key={lawyer.id.toString()} className="group">
                                        <div className="relative overflow-hidden bg-gray-50 mb-8">
                                            <div className="aspect-[4/5] bg-gradient-to-b from-gray-100 to-gray-200 flex items-center justify-center">
                                                {lawyer.image_url ? (
                                                    <img
                                                        src={lawyer.image_url}
                                                        alt={`${lawyer.name} 프로필`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="text-6xl font-thin text-gray-400">
                                                        {lawyer.name.charAt(0)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-700"></div>
                                        </div>

                                        <div className="space-y-6">
                                            <div className="border-l-2 border-red-600 pl-6">
                                                <h3 className="text-2xl font-light text-gray-900 mb-1">{lawyer.name}</h3>
                                                <p className="text-sm text-gray-500 uppercase tracking-wider">{lawyer.position}</p>
                                            </div>

                                            <div className="space-y-5 text-sm leading-relaxed">
                                                {/* 경력 */}
                                                {lawyer.career && lawyer.career.length > 0 && (
                                                    <div>
                                                        <h4 className="text-gray-900 font-medium mb-3 text-xs uppercase tracking-wider">Career</h4>
                                                        <div className="space-y-2">
                                                            {lawyer.career.slice(0, 4).map((item: string, idx: number) => (
                                                                <p key={idx} className="text-gray-600 text-sm leading-relaxed">
                                                                    {item}
                                                                </p>
                                                            ))}
                                                            {lawyer.career.length > 4 && (
                                                                <p className="text-gray-400 text-xs italic">
                                                                    +{lawyer.career.length - 4} more
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* 학력 */}
                                                {lawyer.education && lawyer.education.length > 0 && (
                                                    <div>
                                                        <h4 className="text-gray-900 font-medium mb-3 text-xs uppercase tracking-wider">Education</h4>
                                                        <div className="space-y-2">
                                                            {sortEducation(lawyer.education).slice(0, 3).map((item: string, idx: number) => (
                                                                <p key={idx} className="text-gray-600 text-sm leading-relaxed">
                                                                    {item}
                                                                </p>
                                                            ))}
                                                            {lawyer.education.length > 3 && (
                                                                <p className="text-gray-400 text-xs italic">
                                                                    +{lawyer.education.length - 3} more
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* 모바일 버전 - 1명씩 슬라이드 */}
                            <div className="lg:hidden relative">
                                <div className="overflow-hidden">
                                    <div
                                        className="flex transition-transform duration-300 ease-in-out"
                                        style={{ transform: `translateX(-${currentMobileLawyerIndex * 100}%)` }}
                                    >
                                        {lawyers.map((lawyer: lawyers, index: number) => (
                                            <div key={lawyer.id.toString()} className="w-full flex-shrink-0 px-4">
                                                <div className="group max-w-sm mx-auto">
                                                    <div className="relative overflow-hidden bg-gray-50 mb-6">
                                                        <div className="aspect-[4/5] bg-gradient-to-b from-gray-100 to-gray-200 flex items-center justify-center">
                                                            {lawyer.image_url ? (
                                                                <img
                                                                    src={lawyer.image_url}
                                                                    alt={`${lawyer.name} 프로필`}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            ) : (
                                                                <div className="text-6xl font-thin text-gray-400">
                                                                    {lawyer.name.charAt(0)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="space-y-6">
                                                        <div className="border-l-2 border-red-600 pl-6">
                                                            <h3 className="text-xl font-light text-gray-900 mb-1">{lawyer.name}</h3>
                                                            <p className="text-sm text-gray-500 uppercase tracking-wider">{lawyer.position}</p>
                                                        </div>

                                                        <div className="space-y-5 text-sm leading-relaxed">
                                                            {/* 경력 */}
                                                            {lawyer.career && lawyer.career.length > 0 && (
                                                                <div>
                                                                    <h4 className="text-gray-900 font-medium mb-3 text-xs uppercase tracking-wider">Career</h4>
                                                                    <div className="space-y-2">
                                                                        {lawyer.career.slice(0, 3).map((item: string, idx: number) => (
                                                                            <p key={idx} className="text-gray-600 text-sm leading-relaxed">
                                                                                {item}
                                                                            </p>
                                                                        ))}
                                                                        {lawyer.career.length > 3 && (
                                                                            <p className="text-gray-400 text-xs italic">
                                                                                +{lawyer.career.length - 3} more
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* 학력 */}
                                                            {lawyer.education && lawyer.education.length > 0 && (
                                                                <div>
                                                                    <h4 className="text-gray-900 font-medium mb-3 text-xs uppercase tracking-wider">Education</h4>
                                                                    <div className="space-y-2">
                                                                        {sortEducation(lawyer.education).slice(0, 3).map((item: string, idx: number) => (
                                                                            <p key={idx} className="text-gray-600 text-sm leading-relaxed">
                                                                                {item}
                                                                            </p>
                                                                        ))}
                                                                        {lawyer.education.length > 3 && (
                                                                            <p className="text-gray-400 text-xs italic">
                                                                                +{lawyer.education.length - 3} more
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* 모바일 네비게이션 버튼 */}
                                {hasPrevMobileLawyer && (
                                    <button
                                        onClick={prevMobileLawyer}
                                        className="absolute left-2 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-gray-900/80 backdrop-blur-sm border border-gray-700 shadow-xl rounded-full transition-all duration-300 z-10 group hover:bg-gray-900 hover:border-gray-600 hover:shadow-2xl"
                                    >
                                        <svg className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-white group-hover:text-gray-200 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path>
                                        </svg>
                                    </button>
                                )}

                                {hasNextMobileLawyer && (
                                    <button
                                        onClick={nextMobileLawyer}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-gray-900/80 backdrop-blur-sm border border-gray-700 shadow-xl rounded-full transition-all duration-300 z-10 group hover:bg-gray-900 hover:border-gray-600 hover:shadow-2xl"
                                    >
                                        <svg className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-white group-hover:text-gray-200 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"></path>
                                        </svg>
                                    </button>
                                )}

                                {/* 모바일 페이지 인디케이터 */}
                                <div className="flex justify-center mt-6 space-x-2">
                                    {lawyers.map((_: lawyers, index: number) => (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentMobileLawyerIndex(index)}
                                            className={`w-2 h-2 rounded-full transition-all duration-300 ${currentMobileLawyerIndex === index
                                                ? 'bg-red-600 w-6'
                                                : 'bg-gray-300 hover:bg-gray-400'
                                                }`}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* 데스크탑 네비게이션 화살표 */}
                            {hasPrevLawyers && (
                                <button
                                    onClick={prevLawyers}
                                    className="hidden lg:block absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 w-14 h-14 bg-gray-900/85 backdrop-blur-sm border border-gray-700 shadow-2xl rounded-full transition-all duration-300 group hover:bg-gray-900 hover:border-gray-600 hover:shadow-3xl"
                                >
                                    <svg className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-white group-hover:text-gray-200 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path>
                                    </svg>
                                </button>
                            )}

                            {hasNextLawyers && (
                                <button
                                    onClick={nextLawyers}
                                    className="hidden lg:block absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 w-14 h-14 bg-gray-900/85 backdrop-blur-sm border border-gray-700 shadow-2xl rounded-full transition-all duration-300 group hover:bg-gray-900 hover:border-gray-600 hover:shadow-3xl"
                                >
                                    <svg className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-white group-hover:text-gray-200 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7"></path>
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* 데스크탑 페이지 인디케이터 */}
                        {lawyers.length > lawyersPerPage && (
                            <div className="hidden lg:flex justify-center mt-12 space-x-2">
                                {Array.from({ length: Math.ceil(lawyers.length / lawyersPerPage) }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentLawyerIndex(i * lawyersPerPage)}
                                        className={`w-3 h-3 rounded-full transition-all duration-300 ${Math.floor(currentLawyerIndex / lawyersPerPage) === i
                                            ? 'bg-red-600'
                                            : 'bg-gray-300 hover:bg-gray-400'
                                            }`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                {/* 미디어 출연 섹션 */}
                <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
                    <div className="max-w-5xl mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">미디어 출연</h2>
                            <p className="text-lg text-gray-600">
                                대중과 소통하며 법률 지식을 전달하는 법무법인 휘상
                            </p>
                        </div>

                        {/* 유튜브 카드 - 세로 레이아웃 */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden youtube-card max-w-4xl mx-auto">
                            {/* 유튜브 영상 상단 배치 */}
                            <div className="relative group cursor-pointer">
                                <a
                                    href="https://youtu.be/p3msgm4mJ7U"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block"
                                >
                                    <div className="aspect-video bg-black relative overflow-hidden">
                                        {/* 썸네일 이미지 */}
                                        <img
                                            src="/images/youtube_thumbnail.jpg"
                                            alt="공룡아빠 채널 출연 - 법무법인 휘상"
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        />

                                        {/* 어두운 오버레이 */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                                        {/* 재생 버튼 */}
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="relative">
                                                <div className="absolute inset-0 bg-red-600 rounded-full blur-xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
                                                <div className="relative w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-300">
                                                    <svg className="w-10 h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M8 5v14l11-7z" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>

                                        {/* 영상 정보 오버레이 - 하단 */}
                                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                            <p className="text-sm font-medium mb-2 opacity-90">공룡아빠 채널 특별 출연</p>
                                            <h3 className="text-xl md:text-2xl font-bold leading-tight">
                                                불륜 증거 수집의 모든 것! 이혼 전문 변호사가 알려주는 실전 팁
                                            </h3>
                                        </div>
                                    </div>
                                </a>

                                {/* 유튜브 라벨 - 좌상단 */}
                                <div className="absolute top-4 left-4 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg pulse-animation flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                    </svg>
                                    YOUTUBE
                                </div>

                                {/* 방송 정보 라벨 - 우상단 */}
                                <div className="absolute top-4 right-4 bg-black/70 backdrop-blur text-white px-3 py-1 rounded-lg text-sm flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                    </svg>
                                    2024.12 방송
                                </div>

                                {/* 클릭 안내 - 중앙 하단 */}
                                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/90 backdrop-blur text-gray-900 px-4 py-2 rounded-full text-sm font-medium shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    클릭하여 YouTube에서 시청하기
                                </div>
                            </div>

                            {/* 콘텐츠 설명 하단 배치 */}
                            <div className="p-8 md:p-10">
                                {/* 채널 정보 */}
                                <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100">
                                    <div className="flex items-center gap-4">
                                        <a
                                            href="https://www.youtube.com/@DinosaurDaddy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-4 hover:opacity-80 transition-opacity"
                                        >
                                            <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg">
                                                <span className="text-white font-bold text-xl">공</span>
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-900">공룡아빠</h3>
                                                <p className="text-red-600 font-medium flex items-center gap-2">
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M10 15l5.88-3.88L10 7v8zm11.97-7.33c-.14-.55-.55-1-.97-1.14C19.84 6.25 12 6.25 12 6.25s-7.84 0-9 .28c-.42.14-.83.59-.97 1.14C1.75 8.83 1.75 12 1.75 12s0 3.17.28 4.33c.14.55.55 1 .97 1.14 1.16.28 9 .28 9 .28s7.84 0 9-.28c.42-.14.83-.59.97-1.14.28-1.16.28-4.33.28-4.33s0-3.17-.28-4.33z" />
                                                    </svg>
                                                    구독자 17만명
                                                </p>
                                            </div>
                                        </a>
                                    </div>

                                    {/* 방송 정보 */}
                                    <div className="text-right">
                                        <div className="flex flex-col items-end gap-1">

                                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                                                화제의 방송
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* 출연 정보 하이라이트 */}
                                <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl p-6 mb-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md">
                                            <span className="text-lg">⚖️</span>
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-900">법무법인 휘상 김민건 변호사 출연</p>
                                            <p className="text-sm text-gray-600">이혼 및 가사 전문 변호사</p>
                                        </div>
                                    </div>
                                </div>

                                {/* 주요 내용 */}
                                <div className="space-y-4 mb-8">
                                    <h5 className="font-bold text-gray-900 mb-3">📌 방송 주요 내용</h5>
                                    <div className="space-y-3">
                                        <div className="flex items-start gap-3">
                                            <span className="text-red-500 mt-0.5">✓</span>
                                            <p className="text-gray-700">
                                                <span className="font-medium">불륜 증거 수집 방법:</span> 법적으로 유효한 증거를 확보하는 실무적인 접근법과 전략
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <span className="text-red-500 mt-0.5">✓</span>
                                            <p className="text-gray-700">
                                                <span className="font-medium">실제 사례 분석:</span> 성공적인 이혼 소송 사례를 통한 현실적인 조언
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <span className="text-red-500 mt-0.5">✓</span>
                                            <p className="text-gray-700">
                                                <span className="font-medium">전문가의 심층 분석:</span> 이혼 소송 과정에서 놓치기 쉬운 중요한 법적 포인트 소개
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* 해시태그 */}
                                <div className="flex flex-wrap gap-2 mb-8">
                                    {['#공룡아빠', '#17만유튜버', '#이혼전문변호사', '#김민건변호사', '#법무법인휘상', '#이혼상담', '#불륜이혼'].map((tag, index) => (
                                        <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-gray-200 transition-colors cursor-pointer">
                                            {tag}
                                        </span>
                                    ))}
                                </div>

                                {/* CTA 버튼 */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <a
                                        href="https://youtu.be/p3msgm4mJ7U"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                        </svg>
                                        YouTube에서 전체 영상 보기
                                    </a>
                                    <button
                                        onClick={() => document.getElementById('survey-section')?.scrollIntoView({ behavior: 'smooth' })}
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
                                    >
                                        <span>💬</span>
                                        무료 법률 상담 신청
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* 추가 미디어 출연 예정 공간 */}
                        <div className="mt-12 text-center">
                            <p className="text-gray-500 text-sm">
                                더 많은 미디어 출연과 법률 콘텐츠가 준비 중입니다
                            </p>
                        </div>
                    </div>
                </section>

                {/* 상담후기 섹션 */}
                <section className="py-20 bg-gray-100">
                    <div className="max-w-6xl mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">상담후기</h2>
                            <p className="text-lg text-gray-600">실제 고객분들의 생생한 후기를 들어보세요</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {[
                                {
                                    name: "김정O",
                                    gender: "남성",
                                    location: "서울강남구",
                                    review: "6개월간 의심만 하다 법무법인에 의뢰했습니다. 체계적인 증거 수집과 소송 진행으로 예상보다 높은 위자료를 받았습니다. 감정 소모 없이 법적 해결에 집중할 수 있어 만족스러웠습니다."
                                },
                                {
                                    name: "이O라",
                                    gender: "여성",
                                    location: "부산해운대구",
                                    review: "남편 불륜 발견 후 법무법인을 찾았습니다. 부족했던 증거를 변호사 조언으로 추가 확보해 4개월 만에 합의금과 사과를 받아냈습니다. 전문가 도움으로 새 출발할 수 있었습니다."
                                },
                                {
                                    name: "박준O",
                                    gender: "남성",
                                    location: "경기도분당",
                                    review: "이혼만 고려했으나 상담 후 상간 소송도 진행했습니다. 증거 보전부터 소송 전략까지 체계적 안내로 적정 위자료와 유리한 재산 분할을 얻었습니다. 어려운 시기에 든든한 지원이었습니다."
                                },
                                {
                                    name: "최O연",
                                    gender: "여성",
                                    location: "대전유성구",
                                    review: "남편과 제3자 상대로 소송 진행 시 법무법인의 법적 조언과 정서적 지지가 큰 도움이 되었습니다. 6개월 소송 끝에 합당한 보상을 받았고, 제 권리를 지킬 수 있어 후회 없는 선택이었습니다."
                                }
                            ].map((item, index) => (
                                <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-gray-100">
                                    {/* 별점 */}
                                    <div className="flex items-center mb-4">
                                        {[...Array(5)].map((_, i) => (
                                            <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 24 24">
                                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                            </svg>
                                        ))}
                                        <span className="ml-2 text-sm text-gray-500 font-medium">5.0</span>
                                    </div>

                                    {/* 후기 내용 */}
                                    <p className="text-gray-700 leading-relaxed mb-6 text-base">
                                        "{item.review}"
                                    </p>

                                    {/* 고객 정보 */}
                                    <div className="pt-4 border-t border-gray-100">
                                        <p className="font-semibold text-gray-900">{item.name}</p>
                                        <p className="text-sm text-gray-500">{item.gender} · {item.location}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* 더 많은 후기 버튼 */}
                        <div className="text-center mt-12">
                            <button
                                onClick={() => document.getElementById('survey-section')?.scrollIntoView({ behavior: 'smooth' })}
                                className="inline-flex items-center px-8 py-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-lg hover:shadow-xl"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                                나도 상담받아보기
                            </button>
                        </div>
                    </div>
                </section>

                {/* 변호사 소개 */}
                <section className="py-20 bg-white">
                    <div className="max-w-6xl mx-auto px-4">
                        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
                            법무법인 휘상이<br />
                            <span className="text-red-600">이혼∙가사 소송의 성공사례가 많았던 이유</span>
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {lawyerFeatures.map((item, index) => (
                                <div key={index} className="bg-gray-50 rounded-lg p-6 text-center shadow hover:shadow-md transition-shadow">
                                    <div className="text-4xl mb-4">{item.icon}</div>
                                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                                    <p className="text-gray-600">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* 상담 안내 */}
                <section className="py-16 bg-red-600 text-white">
                    <div className="max-w-4xl mx-auto px-4 text-center">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">지금 바로 전문 변호사에게 상담받으세요</h2>
                        <p className="text-xl mb-8">첫 상담은 무료로 진행됩니다</p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => document.getElementById('survey-section')?.scrollIntoView({ behavior: 'smooth' })}
                                className="px-8 py-4 bg-white text-red-600 hover:bg-gray-100 rounded-md text-lg font-bold transition-colors"
                            >
                                무료 상담 신청하기
                            </button>
                            <a
                                href="tel:02-6959-2170"
                                className="px-8 py-4 bg-transparent border-2 border-white hover:bg-white/10 text-white rounded-md text-lg font-bold transition-colors flex items-center justify-center"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                </svg>
                                02-6959-2170
                            </a>
                        </div>
                    </div>
                </section>

                {/* 푸터 */}
                <footer className="bg-gray-900 text-white py-12">
                    <div className="max-w-6xl mx-auto px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <h3 className="text-2xl font-bold mb-4">법무법인 휘상</h3>
                                <p className="mb-4">
                                    서울특별시 강남구 영동대로 611 13층 (찬앤찬타워)<br />
                                    대표번호: 02-6959-2170
                                </p>
                                <p className="text-sm text-gray-400">
                                    © {new Date().getFullYear()} 법무법인 휘상. All rights reserved.
                                </p>
                            </div>
                            <div>
                                <h4 className="text-lg font-semibold mb-4">빠른 링크</h4>
                                <ul className="space-y-2">
                                    <li><Link to="/" className="text-gray-300 hover:text-white transition-colors">홈페이지</Link></li>
                                    <li><Link to="/about/introduction" className="text-gray-300 hover:text-white transition-colors">로펌소개</Link></li>
                                    <li><Link to="/practice-areas" className="text-gray-300 hover:text-white transition-colors">업무분야</Link></li>
                                    <li><Link to="/members/lawyers" className="text-gray-300 hover:text-white transition-colors">구성원</Link></li>
                                    <li><Link to="/news" className="text-gray-300 hover:text-white transition-colors">뉴스</Link></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </footer>

                {/* 상담 신청 모달 */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn">
                            <div className="bg-gradient-to-r from-red-600 to-red-800 p-6 text-white">
                                <h3 className="text-2xl font-bold">상담 신청</h3>
                                <p className="mt-1 text-white/80">
                                    {consultType ? `${consultType} 상담을 신청합니다` : '상담 신청서'}
                                </p>
                            </div>

                            <Form method="post" className="p-6">
                                <input type="hidden" name="surveyData" value={JSON.stringify(surveyData)} />

                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                            placeholder="이름을 입력해주세요"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">연락처</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                            placeholder="연락 가능한 번호를 입력해주세요"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">상담내용 (선택)</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            rows={4}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                                            placeholder="추가로 전달하고 싶은 내용이 있으시면 적어주세요"
                                        />
                                    </div>

                                    {/* 설문 응답 요약 표시 */}
                                    {(surveyData.situation || surveyData.reason) && (
                                        <div className="bg-red-50 p-4 rounded-lg">
                                            <h4 className="text-sm font-medium text-red-800 mb-2">설문 응답 요약</h4>
                                            <div className="text-xs text-red-600 space-y-1">
                                                {surveyData.situation && <p>• 현재 상황: {surveyData.situation}</p>}
                                                {surveyData.reason && <p>• 이혼 사유: {surveyData.reason}</p>}
                                                {surveyData.children && <p>• 자녀 유무: {surveyData.children}</p>}
                                                {surveyData.property && <p>• 재산/증거: {surveyData.property}</p>}
                                                {surveyData.priority && <p>• 우선 목표: {surveyData.priority}</p>}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-between">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                    >
                                        취소
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-70"
                                    >
                                        {isSubmitting ? '전송 중...' : '상담 신청하기'}
                                    </button>
                                </div>

                                <p className="mt-4 text-xs text-gray-500 text-center">
                                    제출하신 정보는 상담 목적으로만 사용되며, 법적 보호를 받습니다.
                                </p>
                            </Form>

                            {/* 닫기 버튼 */}
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="absolute top-4 right-4 text-white hover:text-gray-200"
                                aria-label="Close"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                {/* 플로팅 버튼 */}
                <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-40">
                    <a
                        href="tel:02-6959-2170"
                        className="w-14 h-14 rounded-full bg-red-600 text-white flex items-center justify-center shadow-lg hover:bg-red-700 transition-all"
                        aria-label="전화 상담"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                        </svg>
                    </a>

                    <a
                        href="https://pf.kakao.com/_DIVwn"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-14 h-14 rounded-full bg-[#FEE500] text-[#3A1D1D] flex items-center justify-center shadow-lg hover:bg-[#F6DC00] transition-all"
                        aria-label="카카오톡 상담"
                    >
                        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 3C7.03125 3 3 6.03125 3 9.90625C3 12.4687 4.73437 14.7344 7.40625 15.9844C7.17187 16.8281 6.46875 18.9219 6.32812 19.3594C6.14062 19.9219 6.5 19.9062 6.73438 19.7344C6.92188 19.6094 9.40625 17.9688 10.5938 17.1719C11.0469 17.2344 11.5156 17.2656 12 17.2656C16.9687 17.2656 21 14.2344 21 9.90625C21 6.03125 16.9687 3 12 3Z" />
                        </svg>
                    </a>

                    <button
                        onClick={() => document.getElementById('survey-section')?.scrollIntoView({ behavior: 'smooth' })}
                        className="w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-lg hover:bg-blue-700 transition-all"
                        aria-label="상담 신청"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                        </svg>
                    </button>
                </div>

                {/* 스타일 정의 */}
                <style dangerouslySetInnerHTML={{
                    __html: `
                    @keyframes fadeIn {
                        from {
                            opacity: 0;
                            transform: scale(0.95);
                        }
                        to {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }

                    @keyframes slideInUp {
                        from {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes scroll {
                        0% {
                            transform: translateY(0);
                            opacity: 1;
                        }
                        50% {
                            transform: translateY(6px);
                            opacity: 0.5;
                        }
                        100% {
                            transform: translateY(0);
                            opacity: 1;
                        }
                    }

                    @keyframes glow {
                        0% {
                            filter: drop-shadow(0 0 20px rgba(255,255,255,0.5));
                        }
                        50% {
                            filter: drop-shadow(0 0 35px rgba(255,255,255,0.8)) drop-shadow(0 0 20px rgba(254,240,138,0.6));
                        }
                        100% {
                            filter: drop-shadow(0 0 20px rgba(255,255,255,0.5));
                        }
                    }

                    .animate-fadeIn {
                        animation: fadeIn 1.2s ease-out;
                    }

                    .animate-slideInUp {
                        animation: slideInUp 0.4s ease-out;
                    }

                    .animate-scroll {
                        animation: scroll 2s ease-in-out infinite;
                    }

                    .animate-glow {
                        animation: glow 3s ease-in-out infinite;
                    }

                    .survey-option-hover:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 25px rgba(220, 38, 38, 0.15);
                    }

                    .youtube-card {
                        transition: all 0.3s ease;
                    }

                    .youtube-card:hover {
                        transform: translateY(-8px);
                        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
                    }

                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }

                    .pulse-animation {
                        animation: pulse 2s ease-in-out infinite;
                    }

                    @media (max-width: 768px) {
                        .survey-question-mobile {
                            font-size: 1.5rem !important;
                            line-height: 1.4 !important;
                        }
                    }
                `
                }} />
            </div>
        </>
    );
} 